import { Box, Chip, CircularProgress, Typography } from '@mui/material';

import { InfoFieldValue, MutedContent, NO_DATA_PLACEHOLDER } from '@occc/ui-design-system';

import { ReactNode } from 'react';

export type ChipItem = {
  label: string;
  href?: string;
  onClick?: () => void;
};

type ChipListProps = {
  label: string;
  value?: string | number | ReactNode;
  items: ChipItem[];
  countPrefix?: string;
  fullWidth?: boolean;
  emptyPlaceholder?: string;
  isLoading?: boolean;
  disableTypography?: boolean;
};

export const ChipList = ({
  label,
  value,
  items,
  countPrefix,
  fullWidth,
  emptyPlaceholder = NO_DATA_PLACEHOLDER,
  isLoading = false,
  disableTypography,
}: ChipListProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
        flexShrink: 0,
        width: fullWidth ? '100%' : undefined,
      }}
    >
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      {value || value === 0 ? (
        <InfoFieldValue value={value} disableTypography={disableTypography} truncate />
      ) : null}
      <Box display='flex' alignItems='center' flexWrap='wrap' gap={1}>
        {isLoading ? (
          <Box
            display='flex'
            alignItems='center'
            sx={{
              height: (theme) => theme.spacing(4),
            }}
          >
            <CircularProgress size={18} color='primary' />
          </Box>
        ) : (
          <>
            {countPrefix && (
              <Typography variant='body1' color='text.primary' sx={{ mr: 0.5 }}>
                {countPrefix}
              </Typography>
            )}
            {items.map((item, index) => (
              <Chip
                key={index}
                label={item.label}
                variant='outlined'
                color='primary'
                clickable={!!item.onClick || !!item.href}
                onClick={item.onClick}
                component={item.href ? 'a' : 'div'}
                href={item.href}
                target={item.href ? '_blank' : undefined}
              />
            ))}
            {items.length === 0 && <MutedContent>{emptyPlaceholder}</MutedContent>}
          </>
        )}
      </Box>
    </Box>
  );
};
